import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { sharedApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * Seller-feedback widget. Reads the buyer's current feedback vote via
 * `GET /classifieds/sellers/:id/feedback` and posts a new vote (or updates
 * it) via `POST` with `{ positive: true|false, listing_id? }` (Iter-240
 * hotfix — backend now requires the `positive` boolean). Legacy responses
 * that echo `my_vote: 'up'|'down'` are still normalised for UI state.
 * Buyers can vote once per seller — repeated submits swap sides. Feedback
 * from unauthenticated users is short-circuited to a sign-in nudge before
 * any request fires.
 */
export default function SellerFeedbackWidget({ sellerId, listingId }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const key = ["seller-feedback", sellerId];

  const feedbackQ = useQuery({
    queryKey: key,
    queryFn: async () => {
      try {
        const { data } = await sharedApi.get(`/classifieds/sellers/${sellerId}/feedback`);
        return data || {};
      } catch (err) {
        if (err?.response?.status === 404) return {};
        throw err;
      }
    },
    enabled: !!sellerId && !!user,
    staleTime: 30_000,
  });

  const mut = useMutation({
    mutationFn: async (positive) => {
      // Iter-240 hotfix: backend REQUIRES `{positive: true|false}` per
      // BUGFIX_DETAIL_INTERACTIONS_ITER240 (was sending `{vote}` → 422).
      const { data } = await sharedApi.post(
        `/classifieds/sellers/${sellerId}/feedback`,
        { positive, listing_id: listingId || undefined }
      );
      return data;
    },
    onSuccess: () => {
      toast.success("Feedback recorded — thank you.");
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ["ad", listingId] });
    },
    onError: (err) => {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.info("Sign in to leave feedback.");
      } else {
        toast.error(err?.response?.data?.detail || "Couldn't save feedback");
      }
    },
  });

  // Backend may echo either the new `positive` boolean or the legacy
  // `my_vote: 'up'|'down'` string; normalise to a boolean for UI state.
  const myPositive =
    typeof feedbackQ.data?.positive === "boolean"
      ? feedbackQ.data.positive
      : feedbackQ.data?.my_vote === "up"
      ? true
      : feedbackQ.data?.my_vote === "down"
      ? false
      : null;

  const vote = (positive) => {
    if (!user) { toast.info("Sign in to leave feedback."); return; }
    if (!sellerId) return;
    mut.mutate(positive);
  };

  return (
    <div
      data-testid="seller-feedback-widget"
      className="mt-3 flex items-center justify-center gap-2"
    >
      <button
        type="button"
        onClick={() => vote(true)}
        disabled={mut.isPending}
        data-testid="seller-feedback-up"
        aria-pressed={myPositive === true}
        aria-label="Positive feedback"
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${
          myPositive === true
            ? "bg-emerald-500 border-emerald-500 text-white"
            : "bg-white border-soft text-slate-700 hover:border-emerald-500 hover:text-emerald-600"
        }`}
      >
        <ThumbsUp className={`w-3.5 h-3.5 ${myPositive === true ? "fill-current" : ""}`} /> Positive
      </button>
      <button
        type="button"
        onClick={() => vote(false)}
        disabled={mut.isPending}
        data-testid="seller-feedback-down"
        aria-pressed={myPositive === false}
        aria-label="Negative feedback"
        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${
          myPositive === false
            ? "bg-red-500 border-red-500 text-white"
            : "bg-white border-soft text-slate-700 hover:border-red-500 hover:text-red-600"
        }`}
      >
        <ThumbsDown className={`w-3.5 h-3.5 ${myPositive === false ? "fill-current" : ""}`} /> Negative
      </button>
    </div>
  );
}
